import { useMemo, useState } from 'react'
import { Screen } from '@/components/layout'
import { Button } from '@/components/primitives'
import { MetricRow, type MetricSpec } from '@/components/cards'
import { FilterBar, type FilterOption } from '@/components/pickers'
import { List, RecurringRow } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonList, SkeletonMetrics } from '@/components/states'
import { api } from '@/lib/api'
import { t } from '@/lib/copy'
import { formatMoney } from '@/lib/format'
import { useNavigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

type Filter = 'active' | 'priceIncreased' | 'unused' | 'ended'

const FILTERS: FilterOption<Filter>[] = [
  { value: 'active', labelKey: 'recurring.filter.active' },
  { value: 'priceIncreased', labelKey: 'recurring.filter.priceIncreased' },
  { value: 'unused', labelKey: 'recurring.filter.unused' },
  { value: 'ended', labelKey: 'recurring.filter.ended' },
]

/** Detected subscriptions and recurring charges, with the annual cost. */
export function RecurringScreen() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<Filter>('active')
  const series = useAsync(() => api.analysis.recurring(), [])
  const recommendations = useAsync(() => api.recommendations.list({ theme: 'subscriptions' }), [])

  const all = series.state.data ?? []
  const topRecommendation = recommendations.state.data?.find((item) => item.status === 'open')

  const visible = useMemo(() => {
    switch (filter) {
      case 'priceIncreased':
        return all.filter((s) => s.status === 'priceIncreased')
      case 'unused':
        return all.filter((s) => s.status === 'unused')
      case 'ended':
        return all.filter((s) => s.status === 'ended' || s.status === 'cancelledByUser')
      default:
        return all.filter((s) => s.status !== 'ended' && s.status !== 'cancelledByUser')
    }
  }, [all, filter])

  const currency = all[0]?.typicalAmount.currency ?? 'USD'
  const monthly = all.reduce((sum, s) => sum + Math.abs(s.monthlyEquivalent?.amount ?? 0), 0)
  const annual = all.reduce((sum, s) => sum + Math.abs(s.annualCost?.amount ?? 0), 0)

  const metrics: MetricSpec[] = [
    { key: 'monthly', label: t('recurring.metric.monthly'), value: formatMoney({ amount: monthly, currency }, { absolute: true }) },
    { key: 'annual', label: t('recurring.metric.annual'), value: formatMoney({ amount: annual, currency }, { absolute: true }) },
    { key: 'count', label: t('recurring.title'), value: t('recurring.metric.count', { count: all.length }) },
  ]

  const screenState =
    series.state.status === 'error'
      ? 'error'
      : series.state.status === 'loading' && !series.state.data
        ? 'loading'
        : all.length === 0
          ? 'empty'
          : 'ready'

  return (
    <Screen
      id="recurring"
      state={screenState}
      actions={
        topRecommendation ? (
          <Button
            variant="secondary"
            onClick={() => navigate('recommendationDetail', { id: topRecommendation.id })}
          >
            {t('recommendationDetail.title')}
          </Button>
        ) : undefined
      }
    >
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonMetrics count={3} />
          <SkeletonList rows={8} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="recurring.error.title"
          error={series.state.status === 'error' ? series.state.error : undefined}
          onRetry={series.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState titleKey="recurring.empty.title" />
      ) : (
        <div className="stack">
          <MetricRow metrics={metrics} />
          <FilterBar options={FILTERS} value={filter} onChange={setFilter} label={t('recurring.title')} />
          <List>
            {visible.map((entry) => (
              <RecurringRow
                key={entry.id}
                series={entry}
                onSelect={() => navigate('recurringDetail', { id: entry.id })}
              />
            ))}
          </List>
        </div>
      )}
    </Screen>
  )
}
